import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert, LinearProgress, Card, CardContent, Grid } from '@mui/material';
import { CloudUpload, Download, Edit, Delete, ArrowBack, PictureAsPdf, Image,
  Description, InsertDriveFile, AutoAwesome, ContentCopy, CheckCircle } from '@mui/icons-material';
import api from '../../services/api';

const kategorieLabels = {
  zuwendungsbescheid: { label: 'Zuwendungsbescheid', color: 'success' },
  antrag: { label: 'Antrag', color: 'primary' },
  kostenplan: { label: 'Kostenplan', color: 'info' },
  verwendungsnachweis: { label: 'Verwendungsnachweis', color: 'warning' },
  kooperationsvereinbarung: { label: 'Kooperationsvereinbarung', color: 'secondary' },
  honorarvertrag: { label: 'Honorarvertrag', color: 'default' },
  sachbericht: { label: 'Sachbericht', color: 'info' },
  sonstiges: { label: 'Sonstiges', color: 'default' },
};

const getFileIcon = (mimetype) => {
  if (mimetype === 'application/pdf') return <PictureAsPdf sx={{ color: '#D32F2F' }} />;
  if (mimetype?.startsWith('image/')) return <Image sx={{ color: '#1565C0' }} />;
  if (mimetype?.includes('word') || mimetype?.includes('document')) return <Description sx={{ color: '#1565C0' }} />;
  return <InsertDriveFile sx={{ color: '#666' }} />;
};

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const DokumentePage = () => {
  const { projektId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [dokumente, setDokumente] = useState([]);
  const [projektTitel, setProjektTitel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadKategorie, setUploadKategorie] = useState('sonstiges');
  const [dragOver, setDragOver] = useState(false);

  // Detail-Dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDok, setSelectedDok] = useState(null);
  const [detailForm, setDetailForm] = useState({ kategorie: '', notizen: '', zusammenfassung: '' });
  const [uebernahmeLoading, setUebernahmeLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [dRes, pRes] = await Promise.all([
        api.get(`/dokumente/projekt/${projektId}`),
        api.get(`/projekte/${projektId}`)
      ]);
      setDokumente(dRes.data);
      setProjektTitel(pRes.data.titel);
    } catch (err) { setError('Fehler beim Laden'); }
  }, [projektId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('projektId', projektId);
      formData.append('kategorie', uploadKategorie);

      const res = await api.post('/dokumente/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(`"${file.name}" hochgeladen${res.data.zusammenfassung ? ' – Analyse abgeschlossen!' : '.'}`);
      load();

      // Detail-Dialog automatisch öffnen bei Analyse-Ergebnis
      if (res.data.zusammenfassung || res.data.extrahierteDaten) {
        openDetail(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Upload');
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files[0]) handleUpload(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]);
  };

  const openDetail = async (dok) => {
    try {
      const res = await api.get(`/dokumente/${dok.id}`);
      setSelectedDok(res.data);
      setDetailForm({
        kategorie: res.data.kategorie,
        notizen: res.data.notizen || '',
        zusammenfassung: res.data.zusammenfassung || ''
      });
      setDetailOpen(true);
    } catch (err) { setError('Fehler beim Laden der Details'); }
  };

  const handleDetailSave = async () => {
    try {
      await api.put(`/dokumente/${selectedDok.id}`, detailForm);
      setDetailOpen(false);
      setSuccess('Dokument aktualisiert');
      load();
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Dokument wirklich löschen?')) {
      try {
        await api.delete(`/dokumente/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  const handleDownload = async (dok) => {
    try {
      const res = await api.get(`/dokumente/${dok.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', dok.dateiname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError('Fehler beim Download'); }
  };

  const handleUebernahme = async (typ) => {
    setUebernahmeLoading(true);
    try {
      let endpoint = '';
      if (typ === 'kostenpositionen') endpoint = `/dokumente/${selectedDok.id}/kostenpositionen-uebernehmen`;
      else if (typ === 'antragsdaten') endpoint = `/dokumente/${selectedDok.id}/antragsdaten-uebernehmen`;
      else if (typ === 'bescheiddaten') endpoint = `/dokumente/${selectedDok.id}/bescheiddaten-uebernehmen`;

      const res = await api.post(endpoint);
      setSuccess(res.data.message);
      setDetailOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler bei der Datenübernahme');
    }
    setUebernahmeLoading(false);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatCurrency = (val) => val ? parseFloat(val).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-';

  const renderExtrahierteDaten = (daten, kategorie) => {
    if (!daten) return <Typography color="text.secondary">Keine Daten extrahiert</Typography>;

    if (kategorie === 'kostenplan' && daten.positionen) {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Erkannte Kostenpositionen:</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Betrag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {daten.positionen.map((p, i) => (
                  <TableRow key={i}><TableCell>{p.bezeichnung}</TableCell><TableCell align="right">{formatCurrency(p.betrag)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {daten.gesamtsumme && <Typography variant="body2"><strong>Gesamtsumme:</strong> {formatCurrency(daten.gesamtsumme)}</Typography>}
        </Box>
      );
    }

    if (kategorie === 'zuwendungsbescheid') {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Erkannte Bescheiddaten:</Typography>
          {daten.foerderkennzeichen && <Typography variant="body2"><strong>Förderkennzeichen:</strong> {daten.foerderkennzeichen}</Typography>}
          {daten.bewilligteSumme && <Typography variant="body2"><strong>Bewilligte Summe:</strong> {formatCurrency(daten.bewilligteSumme)}</Typography>}
          {daten.eigenanteil && <Typography variant="body2"><strong>Eigenanteil:</strong> {formatCurrency(daten.eigenanteil)}</Typography>}
          {daten.laufzeitVon && <Typography variant="body2"><strong>Laufzeit:</strong> {daten.laufzeitVon} bis {daten.laufzeitBis}</Typography>}
          {daten.auflagen?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight="bold">Auflagen:</Typography>
              {daten.auflagen.map((a, i) => <Typography key={i} variant="body2" sx={{ pl: 2 }}>- {a}</Typography>)}
            </Box>
          )}
        </Box>
      );
    }

    if (kategorie === 'antrag') {
      return (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Erkannte Antragsdaten:</Typography>
          {daten.projektTitel && <Typography variant="body2"><strong>Projekttitel:</strong> {daten.projektTitel}</Typography>}
          {daten.antragsteller && <Typography variant="body2"><strong>Antragsteller:</strong> {daten.antragsteller}</Typography>}
          {daten.beantragteSumme && <Typography variant="body2"><strong>Beantragte Summe:</strong> {formatCurrency(daten.beantragteSumme)}</Typography>}
          {daten.kurzbeschreibung && <Typography variant="body2" sx={{ mt: 1 }}><strong>Beschreibung:</strong> {daten.kurzbeschreibung}</Typography>}
        </Box>
      );
    }

    // Allgemein
    return (
      <Box>
        {daten.betraege?.length > 0 && (
          <Typography variant="body2"><strong>Erkannte Beträge:</strong> {daten.betraege.map(b => formatCurrency(b)).join(', ')}</Typography>
        )}
        {daten.daten?.length > 0 && (
          <Typography variant="body2"><strong>Erkannte Daten:</strong> {daten.daten.join(', ')}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/projekte/${projektId}`)} sx={{ color: '#1B5E20' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">Dokumente</Typography>
          <Typography variant="body2" color="text.secondary">{projektTitel}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Upload-Zone */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed', borderColor: dragOver ? '#1B5E20' : '#C8E6C9',
                  borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
                  bgcolor: dragOver ? '#E8F5E9' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#E8F5E9', borderColor: '#1B5E20' }
                }}>
                <CloudUpload sx={{ fontSize: 48, color: '#1B5E20', mb: 1 }} />
                <Typography fontWeight="bold" color="#1B5E20">
                  Datei hierher ziehen oder klicken
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max. 20 MB)
                </Typography>
                <input ref={fileInputRef} type="file" hidden onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth select label="Kategorie" value={uploadKategorie}
                onChange={(e) => setUploadKategorie(e.target.value)} size="small" sx={{ mb: 2 }}>
                {Object.entries(kategorieLabels).map(([key, val]) =>
                  <MenuItem key={key} value={key}>{val.label}</MenuItem>
                )}
              </TextField>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome sx={{ color: '#1B5E20', fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary">
                  PDFs werden automatisch analysiert
                </Typography>
              </Box>
            </Grid>
          </Grid>
          {uploading && <LinearProgress sx={{ mt: 2, '& .MuiLinearProgress-bar': { bgcolor: '#1B5E20' } }} />}
        </CardContent>
      </Card>

      {/* Dokumenten-Liste */}
      <Typography variant="h5" fontWeight="bold" color="#1B5E20" sx={{ mb: 2 }}>
        Hochgeladene Dokumente ({dokumente.length})
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Datei</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Kategorie</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Hochgeladen</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Größe</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Zusammenfassung</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dokumente.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Dokumente hochgeladen</TableCell></TableRow>
            ) : dokumente.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getFileIcon(d.dateityp)}
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{d.dateiname}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={kategorieLabels[d.kategorie]?.label || d.kategorie}
                    color={kategorieLabels[d.kategorie]?.color || 'default'} size="small" />
                </TableCell>
                <TableCell>{formatDate(d.createdAt)}</TableCell>
                <TableCell>{formatSize(d.dateigroesse)}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                    {d.zusammenfassung || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleDownload(d)} sx={{ color: '#1B5E20' }}><Download /></IconButton>
                  <IconButton size="small" onClick={() => openDetail(d)} sx={{ color: '#1B5E20' }}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail-Dialog */}
      {selectedDok && (
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getFileIcon(selectedDok.dateityp)}
              {selectedDok.dateiname}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth select label="Kategorie" value={detailForm.kategorie}
                  onChange={(e) => setDetailForm({...detailForm, kategorie: e.target.value})} margin="normal" size="small">
                  {Object.entries(kategorieLabels).map(([key, val]) =>
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Größe: {formatSize(selectedDok.dateigroesse)}</Typography>
                  {selectedDok.hochgeladenVonUser && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Von: {selectedDok.hochgeladenVonUser.firstName} {selectedDok.hochgeladenVonUser.lastName}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <TextField fullWidth label="Zusammenfassung" value={detailForm.zusammenfassung}
              onChange={(e) => setDetailForm({...detailForm, zusammenfassung: e.target.value})}
              margin="normal" multiline rows={3} />

            <TextField fullWidth label="Notizen" value={detailForm.notizen}
              onChange={(e) => setDetailForm({...detailForm, notizen: e.target.value})}
              margin="normal" multiline rows={2} />

            {/* Extrahierte Daten */}
            {selectedDok.extrahierteDaten && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AutoAwesome sx={{ color: '#1B5E20' }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="#1B5E20">Extrahierte Daten</Typography>
                  </Box>
                  {renderExtrahierteDaten(selectedDok.extrahierteDaten, selectedDok.kategorie)}

                  {/* Übernahme-Buttons */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedDok.kategorie === 'kostenplan' && selectedDok.extrahierteDaten?.positionen?.length > 0 && (
                      <Button variant="outlined" startIcon={<ContentCopy />} onClick={() => handleUebernahme('kostenpositionen')}
                        disabled={uebernahmeLoading}
                        sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { bgcolor: '#E8F5E9' } }}>
                        Kostenpositionen ins Projekt übernehmen
                      </Button>
                    )}
                    {selectedDok.kategorie === 'zuwendungsbescheid' && (
                      <Button variant="outlined" startIcon={<CheckCircle />} onClick={() => handleUebernahme('bescheiddaten')}
                        disabled={uebernahmeLoading}
                        sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { bgcolor: '#E8F5E9' } }}>
                        Bescheiddaten übernehmen
                      </Button>
                    )}
                    {selectedDok.kategorie === 'antrag' && selectedDok.antragId && (
                      <Button variant="outlined" startIcon={<CheckCircle />} onClick={() => handleUebernahme('antragsdaten')}
                        disabled={uebernahmeLoading}
                        sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { bgcolor: '#E8F5E9' } }}>
                        Antragsdaten übernehmen
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Abbrechen</Button>
            <Button onClick={handleDetailSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DokumentePage;
